import React, { useState, useEffect } from 'react';
import { useLazyQuery, useLazyCommand, useEvent, useDomainEvent } from '@soap/modules';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

interface EditTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tagId: string;
  onTagUpdated?: () => void;
}

const viewStates = {
  closed: 0,
  loading: 1,
  ready: 2,
  submitting: 3
};

export function EditTagDialog({ open, onOpenChange, tagId, onTagUpdated }: EditTagDialogProps) {
  const [viewState, setViewState] = useState(viewStates.closed);
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');

  const [getFormData] = useLazyQuery('C124v1_GetC123FormData');
  const [sendCommand] = useLazyCommand('C123v1_EditTag');
  const triggerCloseContextMenu = useDomainEvent({
    eventName: "close-context-menu",
  });

  // Fetch form data when dialog opens
  useEffect(() => {
    if (open && viewState === viewStates.closed && tagId) {
      triggerCloseContextMenu();
      setViewState(viewStates.loading);
      getFormData({
        C124_TagId: tagId
      });
    }
  }, [open, viewState, tagId, getFormData, triggerCloseContextMenu]);

  // Listen for form data event
  useEvent({
    eventName: 'E107v1_GotC123FormData',
    onEventReceived: (event: any) => {
      const fieldData = event.e000_FieldData || [];
      const tagNameField = fieldData.find((field: any) => field.name === 'c123_NewTagName');
      const tagDescriptionField = fieldData.find((field: any) => field.name === 'c123_NewTagDescription');

      setTagName(tagNameField?.initialValue || '');
      setTagDescription(tagDescriptionField?.initialValue || '');
      setViewState(viewStates.ready);
    }
  }, []);

  // Listen for successful update event
  useEvent({
    eventName: 'E108v1_TagUpdated',
    onEventReceived: () => {
      if (viewState === viewStates.submitting) {
        setViewState(viewStates.closed);
        setTagName('');
        setTagDescription('');
        onOpenChange(false);
        onTagUpdated?.();
      }
    }
  }, [viewState, onOpenChange, onTagUpdated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tagName.trim() || !tagId) return;

    setViewState(viewStates.submitting);
    sendCommand({
      $type: 'C123v1_EditTag',
      C123_TagId: tagId,
      C123_NewTagName: tagName.trim(),
      C123_NewTagDescription: tagDescription.trim()
    });
  };

  const handleClose = () => {
    if (viewState !== viewStates.submitting) {
      setViewState(viewStates.closed);
      setTagName('');
      setTagDescription('');
      onOpenChange(false);
    }
  };

  if (!open) return null;

  switch (viewState) {
    case viewStates.loading:
      return (
        <Dialog open={open} onOpenChange={handleClose}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Tag</DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
            </div>
          </DialogContent>
        </Dialog>
      );

    case viewStates.ready:
    case viewStates.submitting:
      return (
        <Dialog open={open} onOpenChange={handleClose}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Tag</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tagName">Tag Name</Label>
                <Input
                  id="tagName"
                  value={tagName}
                  onChange={(e) => setTagName(e.target.value)}
                  required
                  placeholder="Enter tag name"
                  disabled={viewState === viewStates.submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tagDescription">Description (Optional)</Label>
                <Input
                  id="tagDescription"
                  value={tagDescription}
                  onChange={(e) => setTagDescription(e.target.value)}
                  placeholder="Enter description"
                  disabled={viewState === viewStates.submitting}
                />
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleClose} 
                  disabled={viewState === viewStates.submitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={!tagName.trim() || viewState === viewStates.submitting}
                >
                  {viewState === viewStates.submitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      );

    default:
      return null;
  }
}

